import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const load = (k) => { try { return JSON.parse(localStorage.getItem(k)) || []; } catch { return []; } };

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("jigel_cart"));
  const [wishlist, setWishlist] = useState(() => load("jigel_wishlist"));

  useEffect(() => { localStorage.setItem("jigel_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem("jigel_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const addToCart = useCallback((product, size, qty = 1) => {
    setItems((prev) => {
      const key = `${product.product_id}:${size || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => i.key === key ? { ...i, qty: Math.min(i.qty + qty, 10) } : i);
      return [...prev, { key, product_id: product.product_id, name: product.name, brand: product.brand,
        price: product.price, mrp: product.mrp, image: product.images?.[0], size, qty }];
    });
    toast.success("Added to cart", { description: product.name });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) => qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => i.key === key ? { ...i, qty } : i));
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setItems([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.product_id === product.product_id);
      if (exists) { toast("Removed from wishlist"); return prev.filter((p) => p.product_id !== product.product_id); }
      toast.success("Saved to wishlist", { description: product.name });
      return [...prev, { product_id: product.product_id, name: product.name, brand: product.brand,
        price: product.price, mrp: product.mrp, image: product.images?.[0], is_preloved: product.is_preloved, condition: product.condition }];
    });
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addToCart, updateQty, removeItem, clearCart, subtotal, count, wishlist, toggleWishlist }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
